import React, { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  Users, Shuffle, Pin,
  DollarSign, Hash, Layers, Trash2,
  LineChart, Target, BarChart3,
  Edit3, ListTodo, Upload,
  Sun, Moon, LogOut,
  ChevronsLeft, ChevronsRight,
  type LucideIcon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const COLLAPSE_KEY = 'hf-sidebar-collapsed';

interface NavGroupSpec {
  label: string;
  items: { to: string; label: string; Icon: LucideIcon; end?: boolean }[];
}

const NAV_GROUPS: NavGroupSpec[] = [
  {
    label: 'Discovery',
    items: [
      { to: '/models', label: 'Models',     Icon: Users },
      { to: '/feed',   label: 'Swiper',     Icon: Shuffle },
      { to: '/pins',   label: 'Model Pins', Icon: Pin },
    ]
  },
  {
    label: 'Operations',
    items: [
      { to: '/ppc',           label: 'PPC Ordering',  Icon: DollarSign },
      { to: '/meta',          label: 'Meta Tags',     Icon: Hash },
      { to: '/page-versions', label: 'Page Versions', Icon: Layers },
      { to: '/photo-cleanup', label: 'Photo Cleanup', Icon: Trash2 },
    ]
  },
  {
    label: 'Analytics',
    items: [
      // Each page has internal SubTabs (Overview / By page); the sidebar
      // links to the canonical entry point (Overview for both).
      { to: '/analytics',        label: 'Dashboard', Icon: BarChart3 },
      { to: '/traffic',          label: 'Traffic',   Icon: LineChart },
      { to: '/cpc',              label: 'CPC',       Icon: Target    },
    ]
  },
  {
    label: 'Content',
    items: [
      { to: '/blog/posts',   label: 'Blog',    Icon: Edit3 },
      { to: '/backlog',      label: 'Backlog', Icon: ListTodo },
      { to: '/files/upload', label: 'Files',   Icon: Upload },
    ]
  }
];

const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme();
  return (
    <div className="hf-theme-toggle" role="group" aria-label="Theme">
      <button
        type="button"
        className={theme === 'light' ? 'on' : ''}
        onClick={() => setTheme('light')}
        aria-pressed={theme === 'light'}
      >
        <Sun size={12} /> <span>Light</span>
      </button>
      <button
        type="button"
        className={theme === 'dark' ? 'on' : ''}
        onClick={() => setTheme('dark')}
        aria-pressed={theme === 'dark'}
      >
        <Moon size={12} /> <span>Dark</span>
      </button>
    </div>
  );
};

const AppLayout: React.FC = () => {
  const { logout } = useAuth();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  return (
    <div className="hf-app">
      <aside className={`hf-sidebar${collapsed ? ' collapsed' : ''}`}>
        <div className="hf-brand">
          <span className="hf-brand-dot">S</span>
          <span>Seeker</span>
        </div>

        <nav className="hf-nav">
          {NAV_GROUPS.map(group => (
            <div className="hf-nav-group" key={group.label}>
              <div className="hf-nav-label">{group.label}</div>
              {group.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) => `hf-nav-item ${isActive ? 'active' : ''}`}
                >
                  <span className="hf-nav-icon"><item.Icon size={15} /></span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="hf-sidebar-footer">
          {!collapsed && <ThemeToggle />}
          <button
            type="button"
            className="hf-sidebar-toggle"
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronsRight size={14} /> : <><ChevronsLeft size={14} /><span>Collapse</span></>}
          </button>
          <button type="button" className="hf-user" onClick={logout} title={collapsed ? 'Sign out' : undefined}>
            <span className="hf-avatar">A</span>
            <span className="hf-user-info">
              <span className="hf-user-name">admin</span>
              <span className="hf-user-action">
                <LogOut size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />Sign out
              </span>
            </span>
          </button>
        </div>
      </aside>

      <main className="hf-main">
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;
